import jwt_decode from "jwt-decode";
import { snapshot } from "valtio";
import format from "date-fns/format";

import { ListingContractItemDto } from "common/middleware/api.services";
import store from "common/middleware/store";

import enLocale from "common/localization/en-US/checkout.json";
import frLocale from "common/localization/fr/checkout.json";

export const getExternalChangeUnitUrl = (locale: string, isLot?: boolean): string => {
  if (locale == "fr") {
    if(isLot){
      return process.env.NEXT_PUBLIC_CHANGE_LOT_FR_URL;
    }
    else {
      return process.env.NEXT_PUBLIC_CHANGE_UNIT_FR_URL;
    }
  }

  if(isLot){
    return process.env.NEXT_PUBLIC_CHANGE_LOT_EN_URL
  }
  else {
    return process.env.NEXT_PUBLIC_CHANGE_UNIT_EN_URL;
  }
};

export const currencyFormatLocale = (num, locale?): string => {
  num = num ? num : 0;
  if (locale === "fr") {
    return num.toLocaleString("fr-CA", {
      style: "currency",
      currency: "CAD",
    });
  } else {
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "CAD",
    });
  }

  // return num
  //   ? "$" + Intl.NumberFormat("en-US").format(num.toFixed(2))
  //   : "$0.00";
};

export const getAccountFromToken = (token: string): string => {
  return jwt_decode<any>(token).sub;
};

export const formatDateMonthYear = (locale: string, date?: Date): string => {
  if (date) {
    return date.toLocaleDateString(locale === "fr" ? "fr-CA" : locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } else return "";
};

export const formatMonthDateYear = (locale: string, date?: Date): string => {
  if (date) {
    return date.toLocaleDateString(locale === "fr" ? "fr-CA" : locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } else return "";
};

export const formatDateAndTimezone = (locale, apiDateString): string => {
  const date = new Date(apiDateString);

  const month = date.toLocaleString(locale, { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes();
  const ampm = date.getHours() >= 12 ? 'pm' : 'am';

  const timeZone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(date).find(part => part.type === 'timeZoneName').value;

  const formattedDate = locale === 'en-US'
    ? `${month} ${day}, ${year} at ${hours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm} (${timeZone})`
    : `${day} ${month} ${year} à ${hours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm} (${timeZone})`;

  return formattedDate;
}

export const formatSquareRoot = (sqrt?: number): string => {
  if (sqrt !== undefined) {
    sqrt = Math.floor(sqrt);
    return sqrt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  else return "0";
};

export const formatLotNumber = (lotNum: string): string => {
  if (lotNum) {
    return lotNum[0] + " " + lotNum.substring(1,4) + " " + lotNum.substring(4,7);
  }
  else return "";
};

export const getTimeRemaining = (expiryDate: Date): { hours: string, mins: string } => {
  const now = new Date();

  if (!expiryDate || now > expiryDate) {
    return { hours: "00", mins: "00" };
  }

  const diff = expiryDate.getTime() - now.getTime();
  const mins = Math.floor(diff/(1000 * 60) % 60);
  const hours = Math.floor(diff/(1000 * 60 * 60));

  const padDigits = (num) => num.toString().padStart(2, "0");

  return { hours: padDigits(hours), mins: padDigits(mins) };
};

export const localizeHref = (locale, router) => {
  let id = "";
  let pathname = router.pathname;

  if ((router.asPath).includes("?")) {
    id = "?" + router.asPath.split('?')[1];
  } else if ((pathname).includes("[id]")) {
    pathname = pathname.split("/[id]")[0];
    id = "/" + router.query.id as string;
  }

  return locale === "fr" ? frLocale[pathname] + id : enLocale[pathname] + id
}

export const sliceQueriesFromUrl = (url: string) => (
  url.includes("?") ? url.slice(url.indexOf("?")) : ""
);

export const normalizeCharsWithDiacritics = (string: string) => (
  string.normalize("NFD").replace(/\p{Diacritic}/gu, "")
)

export const capitalizeWord = (string: string) => (
  string
    ? string.charAt(0).toUpperCase() + string.slice(1)
    : undefined
)

export const uncapitalizeWord = (string: string) => (
  string.charAt(0).toLowerCase() + string.slice(1)
)

export const formatDateToYYYYMMDD = (date?: Date): string | undefined => {
  if (!date) return undefined;

  return format(date, "yyyy-MM-dd");
}

export const convertFileToBase64 = (file: File): Promise<string | ArrayBuffer> => (
  new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
}));

export const downloadFile = async (filePath: string, saveAs: string) => {
  const res = await fetch(filePath);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = saveAs;
  link.click();
};

export const displaySelectedUnits = (properties: any): string[] => {
  const grouped: Record<string, string[]> = properties.reduce((acc, item) => {
    const key = `${item.projectName} ${item.propertyName}`;
    acc[key] = acc[key] || [];
    acc[key].push(item.unitNumber);
    return acc;
  }, {});

  const result: string[] = [];
  Object.entries(grouped).forEach(([key, unitNumbers]) => {
    result.push(`${key} ${unitNumbers.join(', ')}`);
  });

  return result;
};

export const removeHttpProtocolFromString = (url: string) => {
  return url.replace(/^https?:\/\//, '');
}

export const getCheckoutUrl = (projectName: string) => {
  const { isDevEnv } = snapshot(store);

  const isDev = window.location.origin.includes("dev");
  const isDemo = window.location.origin.includes("demo");

  const project = normalizeCharsWithDiacritics(
    projectName.toLowerCase().replaceAll(" ", "")
  );

  return isDevEnv
    ? isDemo
      ? `https://demo.${project}.ownspace.com`
      : isDev
        ? `https://dev.${project}.ownspace.com`
        : "http://localhost:3000"
    : `https://${project}.ownspace.com`;
}

export const getNumOfDaysFromDate = (date: Date) => {
  if (!date) return 0;

  return Math.abs(Math.ceil(
    (new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  ));
}

export const isFileSizeGreaterThan1MB = (fileSize: number): boolean => {
  const fileSizeInMB = fileSize / (1024 * 1024);

  return fileSizeInMB > 1;
}

export const feetAndInchesToMeters = (feet: number, inches: number): number => {
  if (isNaN(feet) && isNaN(inches)) {
    return;
  }

  const feetInMeters = isNaN(feet) ? 0 : (feet * 0.3048);
  const inchesInMeters = isNaN(inches) ? 0 : (inches * 0.0254);

  return Number((feetInMeters + inchesInMeters).toFixed(2));
}

export const metersToFeetAndInches = (meters: number): { feet: number; inches: number; } => {
  if (isNaN(meters)) {
    return;
  }
  const totalInches = meters * 39.3701;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);

  if (inches === 12) {
    feet += 1;
    inches = 0;
  }

  return { feet, inches };
}

export const sqFeetToSqMeters = (sqFeet: number): number => {
  if (isNaN(sqFeet)) {
    return 0;
  }
  const conversionFactor = 0.09290304;
  return Math.round(sqFeet * conversionFactor * 1e6) / 1e6;
}

export const sqMetersToSqFeet = (sqMeters: number): number => {
  if (isNaN(sqMeters)) {
    return 0;
  }
  const conversionFactor = 10.76391041671;
  return Math.round(sqMeters * conversionFactor * 1e6) / 1e6;
};

export const acresToSqMeters = (acres: number): number => {
  if (!acres) {
    return;
  }
  const sqMeters = acres * 4046.86;

  return Number(sqMeters.toFixed(2));
}

export const sqMetersToAcres = (sqMeters: number): number => {
  if (!sqMeters) {
    return;
  }
  const acres = sqMeters * 0.000247105;

  return Number(acres.toFixed(2));;
}

export const downloadSvgAsPng = (svgElement) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = new Image();

  canvas.width = svgElement.clientWidth;
  canvas.height = svgElement.clientHeight;

  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svg = new Blob([svgData], {
    type: "image/svg+xml;charset=utf-8"
  });
  const url = URL.createObjectURL(svg);

  img.onload = () => {
    ctx.drawImage(img, 0, 0);

    const pngDataUrl = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.download = "ownspace-listing.png";
    link.href = pngDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  img.src = url;
}

export const checkDirtyFields = (fieldsGroups: string[][], dirtyFields: any): boolean[] => {
  return fieldsGroups.map(fields => areFieldsDirty(fields, dirtyFields));
}

export const areFieldsDirty = (fields: string[], dirtyFields: any): boolean => {
  const isFieldDirty = (field, dirtyFields) => {

    if (!dirtyFields || !dirtyFields.hasOwnProperty(field)) {
      return false;
    }

    const fieldValue = dirtyFields[field];

    if (typeof fieldValue === 'boolean') {
      return fieldValue;
    }

    if (typeof fieldValue === 'object') {
      return Object.keys(fieldValue).some(nestedField => isFieldDirty(nestedField, fieldValue));
    }

    return false;
  };

  return fields.some((f) => isFieldDirty(f, dirtyFields));
};

export const formatCountry = (country: string) => {
  if (!country) return;

  return country.length > 3 ? capitalizeWord(country) : country.toUpperCase();
}

export function setNullEmptyStringNaNToUndefined<T extends object>(obj: T): T {
  return Object.entries(obj).reduce((prev, [key, value]) => {
    if (
      value === null
      || value === ""
      || (typeof value === "number" && isNaN(value))
    ) {
      prev[key] = undefined;
    } else {
      prev[key] = typeof value === "object"
        ? setNullEmptyStringNaNToUndefined(value)
        : value;
    }

    return prev;
  }, obj);
}

export const truncateText = (text: string, maxLength: number = 40): string => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength).trimEnd() + "...";
  }
  return text;
}

export const getInclusionsAndExclusions = (
  inclusions: ListingContractItemDto[] = [],
  exclusions: ListingContractItemDto[] = [],
): {
  inclusions: ListingContractItemDto[],
  exclusions: ListingContractItemDto[],
} => {
  return {
    inclusions: [
      ...inclusions.filter(i => i.isSelected),
      ...exclusions.filter(ex => !ex.isSelected),
    ],
    exclusions: [
      ...inclusions.filter(i => !i.isSelected),
      ...exclusions.filter(ex => ex.isSelected),
    ],
  }
}

export const padStart = (
  originalValue: string | number,
  padValue: string,
  targetLength: number,
) => {
  if (
    originalValue === undefined
    || originalValue === null
    || originalValue === ""
  ) {
    return;
  }

  const stringValue = typeof originalValue === "string" ? originalValue : originalValue.toString();

  if (stringValue.length >= targetLength) {
    return stringValue;
  }

  return stringValue.padStart(targetLength, padValue);
}

export const maskData = (
  type: "phone" | "email",
  value: string
) => {
  if (!value) return '';

  if (type === "phone") {
    return value.replace(/\d(?=\d{4})/g, "*");
  }

  if (type === "email") {
    const [email, domain] = value.split("@");

    if (email.length < 2) return value;

    const maskLength = Math.min(email.length - 2, 8);
    const maskedEmail = email.slice(0, 2) + "*".repeat(maskLength);

    return `${maskedEmail}@${domain}`;
  }
}

export const convertArea = (
  value: number,
  conversion: "imperialToMetric" | "metricToImperial",
): number | undefined => {
  if (isNaN(value)) {
    return;
  }

  return conversion === "metricToImperial" ? sqMetersToSqFeet(value) : sqFeetToSqMeters(value);
}

export function convertCurrencyType(value: string | number, convertTo: "string"): string;
export function convertCurrencyType(value: string | number, convertTo: "number"): number;
export function convertCurrencyType(
  value: string | number,
  convertTo: "string" | "number",
) {
  if (!value) return;

  if (typeof value === convertTo) {
    return value;
  }

  if (typeof value === "string") {
    return Number(value.replace(/,/g, ""));
  }

  return value.toLocaleString("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const isEqualUnorderedArrays = (arr1: string[], arr2: string[]) => {
  if (!arr1 && !arr2) {
    return true;
  }

  return arr1?.length === arr2?.length && arr1?.every(item => arr2.includes(item));
};
